import * as THREE from 'three'
import { Suspense, useEffect, useRef, useState } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { Text, useGLTF, AdaptiveDpr, Loader, Stats } from '@react-three/drei'
import { EffectComposer, SSAO, Bloom } from '@react-three/postprocessing'
import { KernelSize, BlendFunction } from 'postprocessing'
import { RectAreaLightUniformsLib, FlakesTexture } from 'three-stdlib'
import { loadGltf } from '../../functions/LoadGltfHelper'
import testModel from './model/test1.glb'
// This demo shows how to use react-three-fibers regression system
// When call call state.regress() nothing really will happen, all it
// does is setting a flag. But parts of the scene graph can now respond
// to it in whatever way the want. Here we cause regression on mouse-move
// and scale the pixel ratio as well as skipping some post-processing effects.

RectAreaLightUniformsLib.init()
THREE.Vector2.prototype.equals = function (v, epsilon = 0.001) {
    return Math.abs(v.x - this.x) < epsilon && Math.abs(v.y - this.y) < epsilon
}

function useLerpedMouse() {
    const mouse = useThree((state) => state.mouse)
    const lerped = useRef(mouse.clone())
    const previous = new THREE.Vector2()
    useFrame((state) => {
        previous.copy(lerped.current)
        lerped.current.lerp(mouse, 0.1)
        // Regress system when the mouse is moved
        if (!previous.equals(lerped.current)) state.performance.regress()
    })
    return lerped
}
let mixer;
const clock = new THREE.Clock();
function YBot(props) {
    const { gl, scene } = useThree()
    const ref = useRef()
    const [texture] = useState(() => new THREE.CanvasTexture(new FlakesTexture(), THREE.UVMapping, THREE.RepeatWrapping, THREE.RepeatWrapping))
    // const { nodes, materials } = useGLTF('./model/test.glb')
    const mouse = useLerpedMouse()
    useFrame((state) => {
        ref.current.rotation.y = (mouse.current.x * Math.PI) / 10
        ref.current.rotation.x = -(mouse.current.y * Math.PI) / 200
        const delta = clock.getDelta();
        if (mixer)
            mixer.update(delta)
    })
    // useEffect(() => {
    //     console.log(nodes)
    // }, [nodes])
    const [gltf, setGltf] = useState(null)
    useEffect(() => {
        if (gl)
            handleModel()
    }, [gl])
    const handleModel = async () => {
        let model = await loadGltf(testModel, gl)
        mixer = new THREE.AnimationMixer(model.scene);
        mixer.clipAction(model.animations[2]).play();
        setGltf(model)
        // scene.add(model.scene)
    }
    return (
        <group ref={ref} dispose={null} {...props}>
 

            <mesh name='model_loaded' position={props.position} >
                {gltf && <primitive object={gltf.scene} />}
            </mesh>
        </group>
    )
}

const Lights = () => {
    const lights = useRef()
    const mouse = useLerpedMouse()
    useFrame((state) => {
        lights.current.rotation.x = (mouse.current.x * Math.PI) / 2
        lights.current.rotation.y = Math.PI * 0.25 - (mouse.current.y * Math.PI) / 2
    })
    return (
        <>
            <directionalLight intensity={1} position={[2, 2, 0]} color="#CFE2F3" distance={5} />
            <spotLight intensity={2} position={[-5, 10, 2]} angle={0.2} penumbra={1} castShadow shadow-mapSize={[2048, 2048]} />
            <group ref={lights}>
                <rectAreaLight intensity={2} position={[4.5, 0, -3]} width={10} height={10} onUpdate={(self) => self.lookAt(0, 0, 0)} />
                <rectAreaLight intensity={2} position={[-10, 2, -10]} width={15} height={15} onUpdate={(self) => self.lookAt(0, 0, 0)} />
            </group>
        </>
    )
}

function Effects() {
    const ref = useRef()
    useFrame((state) => {
        // Disable SSAO on regress
        ref.current.blendMode.setBlendFunction(state.performance.current < 1 ? BlendFunction.SKIP : BlendFunction.MULTIPLY)
    }, [])
    return (
        <EffectComposer multisampling={8}>
            <SSAO ref={ref} intensity={20} radius={0.1} luminanceInfluence={0} bias={0.035} />
            <Bloom kernelSize={KernelSize.LARGE} luminanceThreshold={0.55} luminanceSmoothing={0.2} />
        </EffectComposer>
    )
}
const TextBox = () => {
    const { gl, scene } = useThree()
    const [content, setContent] = useState('Hi!')
    useEffect(() => {
        gl.domElement.addEventListener('mouseover', handleMouseOver)
        gl.domElement.addEventListener('mouseleave', handleMouseLeave)
        return () => {
            gl.domElement.removeEventListener('mouseover', handleMouseOver)
            gl.domElement.removeEventListener('mouseleave', handleMouseLeave)
        }
    }, [])
    const handleMouseOver = () => {
        setContent(`I'm Lan Bao`)
    }
    const handleMouseLeave = () => {
        setContent('Hi!')
    }
    return (
        <Text position={[1, 0, -0.2]} fontSize={0.6} color="white" font="/noto.woff" material-fog={false} letterSpacing={0}>
            {content}
        </Text>
    )
}
export default function Cover() {
    return (//performance={{ min: 0.1 }} 
        <div style={{ width: '100%', height: '100vh', position: 'absolute' }}>
            <Canvas   shadows gl={{ antialias: false }} camera={{ position: [0, 0, 1.5], fov: 75, near: 0.5, far: 100 }}>
                <color attach="background" args={['#EEEEEE']} />
                {/* <fog attach="fog" args={['#CFE2F3', 0.8, 10]} /> */}
                <Lights />
                <Suspense fallback={null}>
                    <YBot position={[-0.5, -0.4, 0]}  />
                    <TextBox />
                </Suspense>
                <mesh scale={7} position={[0, 1, -0.5]} receiveShadow castShadow>
                    <planeGeometry />
                    <meshStandardMaterial color="#EEEEEE" toneMapped={false} fog={false} envMapIntensity={0} />
                </mesh>
                <mesh scale={7} position={[0, -0.8, 0]} rotation={[-Math.PI/2,0,0]} receiveShadow castShadow >
                    <planeGeometry />
                    <meshStandardMaterial color="#B6B6B6"  toneMapped={false}   fog={false} envMapIntensity={0} />
                </mesh>
                <AdaptiveDpr pixelated />
                {/* <Effects /> */}
            </Canvas>
            {/* <Stats/> */}
            <Loader />
        </div>

    )
}
